//@flow
import React from 'react';
import {IconButton, TableCell, TableRow, makeStyles} from '@material-ui/core';
import SaveAltRoundedIcon from '@material-ui/icons/SaveAltRounded';
import CodeRoundedIcon from '@material-ui/icons/CodeRounded';

import {getStringDate} from '../../../../../utils/date';
import {colors} from '../../../../../assets/styles/colors';
import CustomButton from '../../../../../elements/CustomButton/CustomButton';

import {DeleteForever} from '@material-ui/icons';

import type {Theme} from '@material-ui/core';
import type {
  TaxInvoiceDocument,
  TaxInvoiceStateType,
} from '../../../../../entities';

type Props = {
  document: TaxInvoiceDocument,
  onDelete: () => void,
  onDownload: () => void,
};

const useStyles = makeStyles((theme: Theme) => ({
  eInvoiceState: {
    fontSize: '.875rem',
    fontWeight: 600,
    '&[data-state="SCHEDULED"]': {
      color: colors.warning,
    },
    '&[data-state="ACCEPTED"]': {
      color: colors.success,
    },
    '&[data-state="CANCELED"], &[data-state="REJECTED"], &[data-state="FAILED"]': {
      color: colors.error,
    },
    '&[data-state="PENDING"]': {
      color: colors.info,
    },
  },
  orderIdCell: {
    whiteSpace: 'nowrap',
  },
  receiptDocument: {
    fontSize: '.875rem',
    margin: '0 auto',
    width: 'auto',
    display: 'inline-block',
    background: colors.grey200,
    color: colors.grey600,
    padding: '.25rem .5rem',
    borderRadius: '.25rem',
    whiteSpace: 'nowrap',
  },
  rowOptions: {
    display: 'flex',
    alignContent: 'center',
    gap: '.25rem',
  },
  cancelButton: {
    color: colors.error,
  },
  row: {
    whiteSpace: 'nowrap',
  },
}));

const CAN_CANCEL_TAX: TaxInvoiceStateType[] = ['ACCEPTED', 'SCHEDULED'];

export default function TaxInvoiceTableRow(props: Props) {
  const {document, onDelete, onDownload} = props;
  const classes = useStyles();
  return (
    <TableRow>
      <TableCell className={classes.orderIdCell}>
        <CustomButton
          href={`/admin/orders/${document.orderId}`}
          target="_blank"
          rel="noopener noreferrer"
          variant="text"
        >
          {document.orderId}
        </CustomButton>
      </TableCell>
      <TableCell align="center" className={classes.orderIdCell}>
        {document.formattedExternalReferenceId ?? ''}
      </TableCell>
      <TableCell align="center" className={classes.row}>
        {getStringDate(document.createdAt)}
      </TableCell>
      <TableCell align="center">{document.clientName}</TableCell>
      <TableCell align="center">
        <div className={classes.receiptDocument}>
          {document.documentType.label}{' '}
          {document.documentType.code && `| ${document.documentType.code}`}
        </div>
      </TableCell>
      <TableCell align="center">
        <span className={classes.eInvoiceState} data-state={document.state.id}>
          {document.state.label}
        </span>
      </TableCell>
      <TableCell align="center" className={classes.row}>
        {document.orderAmount}
      </TableCell>
      <TableCell align="center" className={classes.rowOptions}>
        <CustomButton
          href={document.pdfUrl}
          target="_blank"
          rel="noopener noreferrer"
          className={classes.downloadReportButton}
          startIcon={<SaveAltRoundedIcon />}
          onClick={onDownload}
          disabled={!document.pdfUrl}
        >
          PDF
        </CustomButton>
        <CustomButton
          href={document.xmlUrl}
          target="_blank"
          rel="noopener noreferrer"
          className={classes.downloadReportButton}
          startIcon={<CodeRoundedIcon />}
          disabled={!document.xmlUrl}
        >
          XML
        </CustomButton>
        <IconButton
          onClick={onDelete}
          size="small"
          className={classes.cancelButton}
          disabled={!CAN_CANCEL_TAX.includes(document.state.id)}
        >
          <DeleteForever />
        </IconButton>
      </TableCell>
    </TableRow>
  );
}
